import React, { useState, useEffect, useCallback } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import uniqueId from 'lodash/uniqueId'
import useDebounce from '../../../hooks/useDebounce'
import AutocompleteResults from './AutocompleteResults'
import AutocompleteForm from './AutocompleteForm'
import FormField from '../FormField'
import Button from '../Button'
import { fetchAutoComplete } from '../../../api/mikasa/requests'
import { TEXT } from '../FormField/types'
import ProperCase from './helpers/properCase'

import styles from './Autocomplete.module.scss'

const propTypes = {
  className: PropTypes.string,
  onSubmit: PropTypes.func
}

const inputId = uniqueId('input')
const listBoxId = uniqueId('listBox')
const resultIdBase = uniqueId('result')
const resultId = index => `${resultIdBase}-${index}`
const describeId = uniqueId('describe')

const messages = defineMessages({
  placeholder: {
    id: 'Autocomplete.address.placeholder',
    defaultMessage: 'Enter a home address'
  }
})

const Autocomplete = ({ className, onSubmit }) => {
  const [input, setInput] = useState('')
  const [selected, setSelected] = useState(null)
  const [results, setResults] = useState(null)
  const [showResults, setShowResults] = useState(false)
  const [loading, setLoading] = useState(false)
  const debouncedInput = useDebounce(input, 750)
  const intl = useIntl()
  const inputMatchesSelection = Boolean(
    results && results[selected] && input === ProperCase({ value: results[selected].fullAddress })
  )

  const activeDescendantId = (() => {
    const loadingId = resultId(0)
    const noResultsId = resultId(0)
    const hasResults = Boolean(results && results.length && selected != null)

    if (showResults) {
      if (loading) return loadingId
      if (!hasResults) return noResultsId
      return resultId(selected)
    }
    return ''
  })()

  const skipFetch = useCallback(param => !param || param.length < 3 || inputMatchesSelection, [inputMatchesSelection])

  const fetchResults = useCallback(
    async param => {
      if (skipFetch(param)) {
        return
      }

      setShowResults(true)

      const resp = await fetchAutoComplete(param)
      setLoading(false)
      setResults(resp.data.data.slice(0, 5))
      setSelected(0)
    },
    [skipFetch]
  )

  useEffect(() => {
    fetchResults(debouncedInput)
  }, [fetchResults, debouncedInput])

  useEffect(() => {
    const skip = skipFetch(input)
    const newInput = input !== debouncedInput
    setLoading(!skip && newInput)
  }, [input, skipFetch, debouncedInput])

  const handleInput = ({ target: { value } }) => {
    setInput(value)

    const skipResults = results && results.length === 0 && skipFetch(value)
    if (value === '' || skipResults) {
      setShowResults(false)
    }
  }

  const handleResultMouseEnter = index => {
    setSelected(index)
  }

  const noResultsToScroll = selected == null || results == null || results.length < 2
  const handleArrowDown = event => {
    if (noResultsToScroll) {
      return
    }

    event.preventDefault()
    const next = selected < results.length - 1 ? selected + 1 : 0
    setSelected(next)
  }

  const handleArrowUp = event => {
    if (noResultsToScroll) {
      return
    }

    event.preventDefault()
    const next = selected > 0 ? selected - 1 : results.length - 1
    setSelected(next)
  }

  const handleEscape = () => {
    if (showResults) {
      setShowResults(false)
    } else {
      setInput('')
    }
    setSelected(null)
  }

  const handleKeyDown = event => {
    const { key } = event
    switch (key) {
      case 'ArrowDown': {
        handleArrowDown(event)
        break
      }
      case 'ArrowUp': {
        handleArrowUp(event)
        break
      }
      case 'Escape': {
        handleEscape(event)
        break
      }
      default: {
      }
    }
  }

  const handleResultClick = index => {
    const result = results[index]
    setInput(ProperCase({ value: result.fullAddress }))
    setShowResults(false)
    setSelected(index)
  }

  return (
    <AutocompleteForm
      className={classnames(styles.root, className)}
      onSubmit={e => {
        e.preventDefault()

        if (results && results[selected] && !loading) {
          onSubmit(results[selected])
        }
      }}
    >
      <div className={styles.wrapper}>
        <FormField
          type={TEXT}
          className={styles.input}
          placeholder={intl.formatMessage(messages.placeholder)}
          value={input}
          selected={selected}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          name='address'
          hideLabel
          id={inputId}
          role='combobox'
          aria-autocomplete='list'
          aria-controls={listBoxId}
          aria-haspopup='true'
          aria-expanded={showResults}
          aria-describedby={describeId}
          aria-activedescendant={activeDescendantId}
        >
          {intl.formatMessage(messages.placeholder)}
        </FormField>
        <Button primary type='submit' className={styles.button}>
          <FormattedMessage id='Autocomplete.button.submit' defaultMessage='Go' />
        </Button>
      </div>
      <AutocompleteResults
        className={styles.results}
        results={results}
        onMouseEnter={handleResultMouseEnter}
        selected={selected}
        onClick={handleResultClick}
        show={showResults}
        boldValue={input}
        spin={loading}
        id={listBoxId}
        resultId={resultId}
      />
      <span id={describeId} className={styles.describe}>
        <FormattedMessage
          id='Autocomplete.describe.instructions'
          defaultMessage='Type 3 characters to initiate address search. When autocomplete results are available use up and down arrows to review and enter to select.'
        />
      </span>
    </AutocompleteForm>
  )
}

Autocomplete.propTypes = propTypes

export default Autocomplete
